import React, { useCallback, useMemo, useState } from 'react';

import { FormControl, IconButton, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { generatePath, useHistory } from 'react-router-dom';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import Table from 'components/Table';
import {
  DPIA_LIFECYCLE_STATUS_FILTER_VALUES,
  DPIA_REQUEST_STATUS_FILTER_VALUES,
  clearDependentDpiaFilters,
} from 'config/api/assetRopa/assetRopaDpiaFilters';
import processingActivitiesApi from 'config/api/processingActivities/processingActivities';
import type { AssetProcessingActivityList } from 'config/api/processingActivities/processingActivities.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import processing_activities_messages from 'messages/processing_activities_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';
import type { TableDataMappingRow } from 'types/Table';

import { getDpiaBadgeClass, resolveApaDpiaDisplayLabel, resolveDpiaBadgeStatus } from '../../_shared/apaDpiaStatus.helpers';
import { canEditDpiaNeedAssessment } from '../../_shared/assetRopaReviewAccess';
import {
  DEFAULT_ASSET_PROCESSING_ACTIVITY_TABLE_FILTERS,
  DEFAULT_ASSET_PROCESSING_ACTIVITY_TABLE_SORT,
  type AssetProcessingActivityTableFilters,
  type AssetProcessingActivityTableSort,
} from '../processingActivityDetail.types';
import {
  buildAssetProcessingActivityListParams,
  filterAssetProcessingActivities,
  hasAssetProcessingActivityFiltersApplied,
  sortAssetProcessingActivities,
} from '../processingActivityDetailFilters';
import useStyles from '../ProcessingActivityDetailPage.styles';

type OfficialReviewStatusMessageKey = keyof typeof processing_activities_messages.detail.official_review_status;

const OFFICIAL_REVIEW_STATUS_FILTER_OPTIONS: OfficialReviewStatusMessageKey[] = [
  'awaiting_response',
  'submitted',
  'clarification_requested',
  'accepted',
];

const DPIA_INDICATION_FILTER_OPTIONS = ['true', 'false'] as const;

type Props = {
  organizationId?: number;
  processingActivityId?: number;
};

const formatDate = (value?: string | null) => (value ? format(new Date(value), 'yyyy-MM-dd') : '—');

const getMessageFromMap = (map: Record<string, string[]>, code: string, t: (message: string | string[]) => string): string => {
  const message = map[code];
  return message ? t(message) : code;
};

const getReviewStatusDotClass = (status: string | undefined, styles: ReturnType<typeof useStyles>) => {
  switch (status) {
    case 'accepted':
      return styles.statusDotActive;
    case 'submitted':
    case 'awaiting_response':
      return styles.statusDotWarning;
    case 'clarification_requested':
      return styles.statusDotDanger;
    default:
      return styles.statusDotInfo;
  }
};

const ProcessingActivityAssetProcessingsSection: React.FC<Props> = ({ organizationId, processingActivityId }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const userData = userDataStorage.get() || {};
  const canAssessDpia = canEditDpiaNeedAssessment(userData);
  const [filters, setFilters] = useState<AssetProcessingActivityTableFilters>(DEFAULT_ASSET_PROCESSING_ACTIVITY_TABLE_FILTERS);
  const [sort, setSort] = useState<AssetProcessingActivityTableSort>(DEFAULT_ASSET_PROCESSING_ACTIVITY_TABLE_SORT);

  const listParams = useMemo(() => buildAssetProcessingActivityListParams(filters, sort), [filters, sort]);

  const assetProcessingsQuery = useQuery(
    [QUERY_KEYS.PROCESSING_ACTIVITIES_ASSET_PROCESSINGS, organizationId, processingActivityId, listParams],
    async () => {
      const { data } = await processingActivitiesApi.getAssetProcessingActivities(
        organizationId as number,
        processingActivityId as number,
        listParams,
      );
      return data;
    },
    {
      enabled: Boolean(organizationId && processingActivityId),
      retry: false,
    },
  );

  const fallbackRowsQuery = useQuery(
    [QUERY_KEYS.PROCESSING_ACTIVITIES_ASSET_PROCESSINGS, organizationId, processingActivityId, 'fallback'],
    async () => {
      const { data } = await processingActivitiesApi.getAssetProcessingActivities(organizationId as number, processingActivityId as number);
      return data.results;
    },
    {
      enabled: Boolean(organizationId && processingActivityId && assetProcessingsQuery.isError),
      retry: false,
    },
  );

  const isLoading = assetProcessingsQuery.isLoading || (assetProcessingsQuery.isError && fallbackRowsQuery.isLoading);
  const useBackendResults = !assetProcessingsQuery.isError;

  const displayRows = useMemo(() => {
    if (useBackendResults) {
      return assetProcessingsQuery.data?.results ?? [];
    }

    const fallbackRows = fallbackRowsQuery.data ?? [];
    const filtered = filterAssetProcessingActivities(fallbackRows, filters);
    return sortAssetProcessingActivities(filtered, sort);
  }, [assetProcessingsQuery.data?.results, fallbackRowsQuery.data, filters, sort, useBackendResults]);

  const totalRowCount = useBackendResults ? assetProcessingsQuery.data?.results.length ?? 0 : fallbackRowsQuery.data?.length ?? 0;
  const hasActiveFilters = hasAssetProcessingActivityFiltersApplied(filters);

  const tableEmptyState = useMemo(() => {
    if (totalRowCount > 0 && hasActiveFilters) {
      return t(processing_activities_messages.detail.empty_state.no_asset_processings_filtered);
    }

    return t(processing_activities_messages.detail.empty_state.no_asset_processings);
  }, [hasActiveFilters, t, totalRowCount]);

  const openAssetProcessingActivity = useCallback(
    (row: AssetProcessingActivityList) => {
      history.push(
        generatePath(PATHS.PROCESSING_ACTIVITIES_ASSET_ROPA_APA, {
          assetRopaId: String(row.assetId),
          apaId: String(row.id),
        }),
        { mode: 'view' },
      );
    },
    [history],
  );

  const assessDpia = useCallback(
    (row: AssetProcessingActivityList) => {
      history.push({
        pathname: generatePath(PATHS.PROCESSING_ACTIVITIES_DPIA_ASSESSMENT, {
          apaId: String(row.id),
        }),
        state: {
          returnPath: history.location.pathname,
        },
      });
    },
    [history],
  );

  const handleRowClick = useCallback(
    (_event: React.MouseEvent, row: AssetProcessingActivityList) => {
      openAssetProcessingActivity(row);
    },
    [openAssetProcessingActivity],
  );

  const handleSort = (key: string, direction: string) => {
    setSort({
      key,
      direction: direction === 'asc' || direction === 'desc' ? direction : null,
    });
  };

  const dataMapping: TableDataMappingRow<AssetProcessingActivityList>[] = useMemo(
    () => [
      {
        id: 'assetName',
        label: t(processing_activities_messages.detail.table.asset),
        get: row => row.assetName,
      },
      {
        id: 'operationalOwnerDisplayName',
        label: t(processing_activities_messages.detail.table.operational_owner),
        get: row => row.operationalOwnerDisplayName || t(processing_activities_messages.detail.table.not_assigned),
      },
      {
        id: 'officialReviewStatus',
        label: t(processing_activities_messages.detail.table.official_review_status),
        get: row => {
          const label = row.officialReviewStatus
            ? getMessageFromMap(processing_activities_messages.detail.official_review_status, row.officialReviewStatus, t)
            : t(processing_activities_messages.review_placeholder);

          return (
            <span>
              <span aria-hidden='true' className={clsx(styles.statusDot, getReviewStatusDotClass(row.officialReviewStatus, styles))} />
              {label}
            </span>
          );
        },
      },
      {
        id: 'updatedAt',
        label: t(processing_activities_messages.detail.table.last_updated),
        get: row => <span className={styles.noWrap}>{formatDate(row.updatedAt)}</span>,
      },
      {
        id: 'dpiaStatus',
        label: t(processing_activities_messages.detail.table.dpia_status),
        get: row => (
          <span
            className={clsx(
              styles.dpiaBadge,
              getDpiaBadgeClass(
                resolveDpiaBadgeStatus(row.dpiaStatus, {
                  dpiaRequestStatus: row.dpiaRequestStatus,
                  dpiaLifecycleStatus: row.dpiaStatus === 'draft' ? 'draft' : null,
                }),
                styles,
              ),
            )}
          >
            {resolveApaDpiaDisplayLabel(row.dpiaStatus, t, {
              dpiaRequestStatus: row.dpiaRequestStatus,
              dpiaLifecycleStatus: row.dpiaStatus === 'draft' ? 'draft' : null,
            })}
          </span>
        ),
      },
      {
        id: 'activePurposesCount',
        label: t(processing_activities_messages.detail.table.active_purposes),
        get: row => row.activePurposesCount,
      },
      {
        id: 'hasSensitivePersonalData',
        label: t(processing_activities_messages.detail.table.sensitive_personal_data),
        get: row =>
          row.hasSensitivePersonalData
            ? t(processing_activities_messages.filters.boolean_options.true)
            : t(processing_activities_messages.filters.boolean_options.false),
      },
      {
        id: 'actions',
        blockSorting: true,
        isAddon: true,
        label: '',
        get: row => (
          <div className={styles.actionsCell}>
            <ContextMenu
              elements={[
                { label: t(processing_activities_messages.actions.open), onClick: () => openAssetProcessingActivity(row) },
                ...(canAssessDpia
                  ? [
                      {
                        label: t(processing_activities_messages.asset_ropa.apa_review.assess_dpia),
                        onClick: () => assessDpia(row),
                      },
                    ]
                  : []),
              ]}
              id={`asset-processing-activity-${row.id}`}
            >
              <IconButton aria-label={t(processing_activities_messages.detail.table.open_actions)} size='small'>
                <MoreIcon />
              </IconButton>
            </ContextMenu>
          </div>
        ),
      },
    ],
    [assessDpia, canAssessDpia, openAssetProcessingActivity, styles, t],
  );

  return (
    <div className={styles.panel}>
      <div className={styles.sectionHeader}>
        <div>
          <Typography className={styles.panelTitle} component='h2' variant='h6'>
            {t(processing_activities_messages.detail.sections.asset_processings)}
          </Typography>
          <div className={styles.sectionHelp}>{t(processing_activities_messages.detail.asset_processings.help)}</div>
        </div>

        <div aria-label={t(processing_activities_messages.detail.asset_processings.filters_aria_label)} className={styles.filterRow}>
          <FormControl className={styles.filterSelect} size='small' variant='outlined'>
            <InputLabel shrink>{t(processing_activities_messages.detail.asset_processings.filters.review_status)}</InputLabel>
            <Select
              displayEmpty
              label={t(processing_activities_messages.detail.asset_processings.filters.review_status)}
              onChange={event => setFilters(current => ({ ...current, officialReviewStatus: String(event.target.value) }))}
              value={filters.officialReviewStatus}
            >
              <MenuItem value=''>{t(processing_activities_messages.detail.asset_processings.filters.all)}</MenuItem>
              {OFFICIAL_REVIEW_STATUS_FILTER_OPTIONS.map(status => (
                <MenuItem key={status} value={status}>
                  {t(processing_activities_messages.detail.official_review_status[status])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={styles.filterSelect} size='small' variant='outlined'>
            <InputLabel shrink>{t(processing_activities_messages.detail.asset_processings.filters.dpia_indication)}</InputLabel>
            <Select
              displayEmpty
              label={t(processing_activities_messages.detail.asset_processings.filters.dpia_indication)}
              onChange={event =>
                setFilters(current =>
                  clearDependentDpiaFilters({
                    ...current,
                    dpiaIndication: String(event.target.value) as AssetProcessingActivityTableFilters['dpiaIndication'],
                  }),
                )
              }
              value={filters.dpiaIndication}
            >
              <MenuItem value=''>{t(processing_activities_messages.detail.asset_processings.filters.all)}</MenuItem>
              {DPIA_INDICATION_FILTER_OPTIONS.map(value => (
                <MenuItem key={value} value={value}>
                  {t(processing_activities_messages.asset_ropa.filters[value === 'true' ? 'yes' : 'no'])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={styles.filterSelect} size='small' variant='outlined'>
            <InputLabel shrink>{t(processing_activities_messages.detail.asset_processings.filters.dpia_request)}</InputLabel>
            <Select
              displayEmpty
              label={t(processing_activities_messages.detail.asset_processings.filters.dpia_request)}
              onChange={event =>
                setFilters(current => ({
                  ...current,
                  dpiaRequestStatus: String(event.target.value) as AssetProcessingActivityTableFilters['dpiaRequestStatus'],
                }))
              }
              value={filters.dpiaRequestStatus}
            >
              <MenuItem value=''>{t(processing_activities_messages.detail.asset_processings.filters.all)}</MenuItem>
              {DPIA_REQUEST_STATUS_FILTER_VALUES.map(status => (
                <MenuItem key={status} value={status}>
                  {t(processing_activities_messages.asset_ropa.dpia_request_status[status])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={styles.filterSelect} size='small' variant='outlined'>
            <InputLabel shrink>{t(processing_activities_messages.detail.asset_processings.filters.dpia_status_lifecycle)}</InputLabel>
            <Select
              displayEmpty
              label={t(processing_activities_messages.detail.asset_processings.filters.dpia_status_lifecycle)}
              onChange={event =>
                setFilters(current => ({
                  ...current,
                  dpiaStatus: String(event.target.value) as AssetProcessingActivityTableFilters['dpiaStatus'],
                }))
              }
              value={filters.dpiaStatus}
            >
              <MenuItem value=''>{t(processing_activities_messages.detail.asset_processings.filters.all)}</MenuItem>
              {DPIA_LIFECYCLE_STATUS_FILTER_VALUES.map(status => (
                <MenuItem key={status} value={status}>
                  {t(processing_activities_messages.asset_ropa.dpia_status[status])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={styles.filterSelect} size='small' variant='outlined'>
            <InputLabel shrink>{t(processing_activities_messages.detail.asset_processings.filters.sensitive_personal_data)}</InputLabel>
            <Select
              displayEmpty
              label={t(processing_activities_messages.detail.asset_processings.filters.sensitive_personal_data)}
              onChange={event => setFilters(current => ({ ...current, hasSensitivePersonalData: String(event.target.value) }))}
              value={filters.hasSensitivePersonalData}
            >
              <MenuItem value=''>{t(processing_activities_messages.detail.asset_processings.filters.all)}</MenuItem>
              <MenuItem value='true'>{t(processing_activities_messages.filters.boolean_options.true)}</MenuItem>
              <MenuItem value='false'>{t(processing_activities_messages.filters.boolean_options.false)}</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      <div
        aria-busy={isLoading}
        aria-label={t(processing_activities_messages.detail.table.aria_label)}
        className={styles.tableScrollWrapper}
        role='region'
      >
        <Table
          clickableRow
          data={displayRows}
          dataMapping={dataMapping}
          emptyStateContent={
            <Typography color='textSecondary' variant='body2'>
              {tableEmptyState}
            </Typography>
          }
          onRowClick={handleRowClick}
          onSort={handleSort}
          refetching={isLoading}
          rowAriaLabel={row => t(processing_activities_messages.detail.table.row_aria_label, { name: row.assetName })}
          sort={sort}
        />
      </div>
    </div>
  );
};

export default ProcessingActivityAssetProcessingsSection;
